import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import SectionHeading from "@/components/feature/SectionHeading";
import MediaCard from "@/components/feature/MediaCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  categories,
  getLatestMedia,
  getMediaByType,
  getTopViewed,
} from "@/lib/media";
import { getTypeLabel } from "@/lib/labels";

export default function Home() {
  useDocumentMeta({
    title: "91视频网站-热门影视资源在线观看平台",
    description:
      "91视频网站提供海量热门电影、电视剧和综艺节目，覆盖多种类型影视内容，方便用户在线观看和发现最新影视资讯。",
    keywords: "91视频网站,影视资源,在线视频观看,电影电视剧,影视平台,在线视频,影视资讯",
    canonicalPath: "/",
  });

  const latest = getLatestMedia(12);
  const ranking = getTopViewed(10);
  const highRated = [...getLatestMedia(40)]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 8);

  return (
    <div className="w-full">
      <HeroCarousel />

      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <SectionHeading
          title="分类快捷入口"
          subtitle="从三大主分类出发，快速进入你的观影主场"
          icon="ri-apps-2-line"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {categories.map((category) => (
            <Link
              key={category.key}
              to={category.path}
              className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-300 hover:bg-primary-50/60 cursor-pointer"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className={`${category.icon} text-2xl`} aria-hidden="true"></i>
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    {category.name}
                  </h3>
                  <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] text-accent-800">
                    {getMediaByType(category.key).length} 部
                  </span>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-500 md:text-sm">
                  {category.intro}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                  进入分类
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <div>
            <SectionHeading
              title="最新影视推荐"
              subtitle="按更新时间排序，第一时间发现新上架内容"
              icon="ri-time-line"
              moreLink="/category/movie"
              moreLabel="更多电影"
            />
            <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-3 xl:grid-cols-4">
              {latest.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          </div>

          <aside>
            <SectionHeading
              title="热门排行榜"
              subtitle="访问量最高的 10 部影片"
              icon="ri-trophy-line"
            />
            <ol className="rounded-lg border border-background-200 bg-background-50 p-2">
              {ranking.map((item, index) => (
                <li key={item.id}>
                  <Link
                    to={`/detail/${item.id}`}
                    className="group flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-primary-50/70 cursor-pointer"
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                        index < 3
                          ? "bg-accent-500 text-background-50"
                          : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground-900 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-foreground-400">
                        {getTypeLabel(item.type)} · {item.year}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-0.5 text-xs font-semibold text-accent-600">
                      <i className="ri-fire-fill text-[11px]" aria-hidden="true"></i>
                      {(item.views / 10000).toFixed(0)}万
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </aside>
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <SectionHeading
          title="高分精选"
          subtitle="口碑炸裂，评分见真章"
          icon="ri-star-smile-line"
          moreLink="/category/tv"
          moreLabel="更多剧集"
        />
        <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-8">
          {highRated.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      <section className="w-full px-4 pb-4 md:px-8">
        <div className="flex flex-col items-start justify-between gap-6 rounded-lg bg-primary-500 px-6 py-8 md:flex-row md:items-center md:px-10">
          <div>
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
              想看的片子找不到？用搜索一步直达
            </h2>
            <p className="mt-2 max-w-xl text-sm text-background-100/90">
              输入片名、类型或演员，91视频网站帮你在海量影视资源中精准定位想看的内容。
            </p>
          </div>
          <Link
            to="/search"
            className="flex shrink-0 items-center gap-2 rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line text-lg" aria-hidden="true"></i>
            立即搜索影视
          </Link>
        </div>
      </section>
    </div>
  );
}